import { useRef } from 'react';

import { CollectionConfirmStep, CollectionDetailsStep, CollectionPartnerSelectStep } from 'pages/partners/pages/collections/components/steps';
import { SelectablePartner } from 'pages/partners/pages/collections/context';
import { GMetrixStepper } from 'components/GMetrixStepper/GMetrixStepper';
import { GMetrixDialog } from 'components/GMetrixDialog/GMetrixDialog';
import { useNotification } from 'app/notifications';
import { CollectionApi } from 'apis/CollectionApi';
import { useSuperState } from 'hooks';
import { useIntl } from 'react-intl';

type CollectionCreateWizardProps = {
    onClose: () => void;
    onSuccess: () => void;
    show: boolean;
};

enum EWizardStep {
    DETAILS,
    PARTNERS,
    CONFIRM
}

export const CollectionCreateWizard = (props: CollectionCreateWizardProps) => {

    const { onClose, onSuccess, show } = props;

    const [state, setState, resetState] = useSuperState({
        name: '',
        createLogin: false,
        description: '',
        email: '',
        firstName: '',
        lastName: '',
        selectedPartners: [] as SelectablePartner[],
        step: EWizardStep.DETAILS,
        submitting: false,
        validationError: ''
    });

    const { formatMessage } = useIntl();
    const { createToast } = useNotification();

    const stepItems = [
        { label: formatMessage({ id: 'basic_information.aa3a7eac', defaultMessage: 'Basic Information' }) },
        { label: formatMessage({ id: 'testing_center.fda9aa47', defaultMessage: 'Testing Center' }) },
        { label: formatMessage({ id: 'confirm_settings.edb3d2c8', defaultMessage: 'Confirm Settings', description: 'Pasos' }) }
    ];

    const handleClose = () => {

        resetState();
        onClose();

    };

    const validateDetailsStep = (): boolean => {

        if (!state.name.trim()) {

            setState({ validationError: 'Association name is required.' });
            return false;

        }

        if (state.createLogin) {

            if (!state.email.trim() || !state.firstName.trim() || !state.lastName.trim()) {

                setState({ validationError: 'Email, first name, and last name are required when creating a login.' });
                return false;

            }

        }

        setState({ validationError: '' });
        return true;

    };

    const validatePartnersStep = (): boolean => {

        if (state.selectedPartners.length < 2) {

            setState({ validationError: formatMessage({ id: 'to_create_a_collection_at_least_2_testin.e198d319', defaultMessage: 'To create a collection, at least 2 Testing Centers must be added.' }) });
            return false;

        }

        setState({ validationError: '' });
        return true;

    };

    const handleNext = () => {

        if (state.step === EWizardStep.DETAILS) {

            if (validateDetailsStep()) {

                setState({ step: EWizardStep.PARTNERS });

            }

        } else if (state.step === EWizardStep.PARTNERS) {

            if (validatePartnersStep()) {

                setState({ step: EWizardStep.CONFIRM });

            }

        }

    };

    const handleBack = () => {

        setState({ validationError: '' });
        if (state.step === EWizardStep.PARTNERS) {

            setState({ step: EWizardStep.DETAILS });

        } else if (state.step === EWizardStep.CONFIRM) {

            setState({ step: EWizardStep.PARTNERS });

        }

    };

    const handleSubmit = async () => {

        try {

            setState({ submitting: true });
            const partnerIds = state.selectedPartners.map(p => p.ID).join(',');

            const response = await CollectionApi.Create(
                state.name,
                state.description,
                partnerIds,
                state.createLogin,
                state.email || undefined,
                state.firstName || undefined,
                state.lastName || undefined
            );

            if (response?.Done) {

                createToast(
                    formatMessage({ id: 'association_created_successfully.68f79515', defaultMessage: 'Association created successfully' }),
                    'success'
                );
                setTimeout(() => {

                    resetState();
                    onSuccess();

                }, 1500);

            } else {

                createToast(
                    response?.Message ?? 'Failed to create association.',
                    'danger'
                );
                setState({ submitting: false });

            }

        } catch (e) {

            createToast(
                formatMessage({ id: 'an_error_occurred_while_creating_the_ass.c8d7abfe', defaultMessage: 'An error occurred while creating the association.' }),
                'danger'
            );
            setState({ submitting: false });

        }

    };

    const getActionButtonText = (): string => {

        if (state.step === EWizardStep.CONFIRM) {

            return state.submitting
                ? formatMessage({ id: 'creating.2155605d', defaultMessage: 'Creating...' })
                : (formatMessage({ id: 'create_association.c3fc4bc4', defaultMessage: 'Create Association' }));

        }

        return formatMessage({ id: 'next.50b7834e', defaultMessage: 'Next' });

    };

    const handleActionClick = () => {

        if (state.step === EWizardStep.CONFIRM) {

            handleSubmit();

        } else {

            handleNext();

        }

    };

    return (
        <>
            <GMetrixDialog
                actionButtonClick={handleActionClick}
                actionButtonDisabled={state.submitting}
                actionButtonText={getActionButtonText()}
                closeButtonText={state.step === EWizardStep.DETAILS ? (formatMessage({ id: 'cancel.89a86adc', defaultMessage: 'Cancel' })) : (formatMessage({ id: 'back.b6238c9e', defaultMessage: 'Back' }))}
                dialogLabel={formatMessage({ id: 'create_association.c3fc4bc4', defaultMessage: 'Create Association' })}
                onCancel={state.step === EWizardStep.DETAILS ? handleClose : handleBack}
                onClose={handleClose}
                show={show}
                size="xl"
            >
                <div className="my-4">
                    <GMetrixStepper
                        handleChange={e => {

                            // Only allow navigating to steps that have been reached
                            if (e.value <= state.step) {

                                setState({ step: e.value, validationError: '' });

                            }

                        }}
                        linear={false}
                        setStep={newStep => setState({ step: newStep as EWizardStep })}
                        step={state.step}
                        stepItems={stepItems}
                    />
                </div>

                {state.validationError && state.step !== EWizardStep.PARTNERS && (
                <div className="alert alert-danger">
                    {state.validationError}
                </div>
                )}

                {state.step === EWizardStep.DETAILS && (
                <CollectionDetailsStep
                    createLogin={state.createLogin}
                    description={state.description}
                    email={state.email}
                    firstName={state.firstName}
                    lastName={state.lastName}
                    name={state.name}
                    onCreateLoginChange={value => setState({ createLogin: value })}
                    onDescriptionChange={value => setState({ description: value })}
                    onEmailChange={value => setState({ email: value })}
                    onFirstNameChange={value => setState({ firstName: value })}
                    onLastNameChange={value => setState({ lastName: value })}
                    onNameChange={value => setState({ name: value })}
                />
                )}

                {state.step === EWizardStep.PARTNERS && (
                <CollectionPartnerSelectStep
                    onSelectedPartnersChange={partners => setState({ selectedPartners: partners })}
                    selectedPartners={state.selectedPartners}
                    validationError={state.validationError}
                />
                )}

                {state.step === EWizardStep.CONFIRM && (
                <CollectionConfirmStep
                    description={state.description}
                    name={state.name}
                    selectedPartners={state.selectedPartners}
                />
                )}
            </GMetrixDialog>
        </>
    );

};
